/**
 * Idea (legacy) from: https://github.com/ueberdosis/tiptap/issues/2305
 */

import { PropsWithChildren, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { BubbleMenu } from '@tiptap/react/menus';
import type { Editor } from '@tiptap/react';
import type { BubbleMenuProps } from '@tiptap/react/menus';
import { Card, CardHeader, makeStyles, tokens } from '@fluentui/react-components';
import { mergeClasses } from '@fluentui/react-components';
import DocumentControlsLock from './DocumentControlsLock';

type TPadding = 'tight' | 'medium' | 'large';

type NodeBubbleMenuShellProps = PropsWithChildren<{
  nodeName: string;
  editor: Editor | null;
  isEdit: boolean;
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
  /** Alternative predicate to determine whether to show the menu */
  alternativeIsOpen?: BubbleMenuProps['shouldShow'];
  /** Extra predicate */
  extraPredicate?: BubbleMenuProps['shouldShow'];
  /** Padding size for the Card */
  paddingCard?: TPadding;
  /** Per-menu width; defaults are kept by caller */
  maxWidth?: string | number;
  /** Optional className for the Card */
  className?: string;
  /** Optional leading icon (placed before header) */
  leadingIcon?: ReactNode;
  /** Optional header content (CardHeader header prop) */
  header?: ReactNode;
}>;

const useStyles = makeStyles({
  card: {
    '& .fui-CardHeader__action': { margin: 0 },
    boxShadow: tokens.shadow16,
    border: `1px solid #aaa`,
    width: 'fit-content',
    // The bubble menu plugin shows the element instantly; fade it in like the rest of the UI.
    animationName: { from: { opacity: 0 }, to: { opacity: 1 } },
    animationDuration: tokens.durationFast,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
  },
  tight: {
    padding: tokens.spacingHorizontalXS,
  },
  medium: {
    padding: tokens.spacingHorizontalS,
  },
  large: {
    padding: tokens.spacingHorizontalM,
  },
});

const NodeBubbleMenuShell = ({
  nodeName,
  editor,
  isEdit,
  isOpen,
  setIsOpen,
  alternativeIsOpen,
  extraPredicate,
  paddingCard = 'large',
  maxWidth = '800px',
  className,
  leadingIcon,
  header,
  children,
}: NodeBubbleMenuShellProps) => {
  const menuRef = useRef<HTMLDivElement>(null);

  const refIsEdit = useRef(isEdit);
  refIsEdit.current = isEdit;

  const refIsOpen = useRef(isOpen);
  refIsOpen.current = isOpen;

  const refAlternativeIsOpen = useRef(alternativeIsOpen);
  refAlternativeIsOpen.current = alternativeIsOpen;

  const refExtraPredicate = useRef(extraPredicate);
  refExtraPredicate.current = extraPredicate;

  // Distinct per menu: the show/hide transaction meta below must target only this menu's plugin.
  const pluginKey = `${nodeName}BubbleMenu`;

  // The plugin dispatches an updateOptions transaction whenever the options identity changes.
  const hideMenu = useCallback(() => setIsOpen?.(false), [setIsOpen]);
  const options = useMemo(() => ({ onHide: hideMenu }), [hideMenu]);

  // The plugin re-evaluates shouldShow only on editor transactions, never on React state alone.
  useEffect(() => {
    if (isOpen === undefined || !editor) return;
    if (isOpen) {
      editor.commands.setMeta(pluginKey, 'show');
      // The plugin's 'show' branch positions before the element is visible - reposition explicitly.
      editor.commands.setMeta(pluginKey, 'updatePosition');
    } else {
      editor.commands.setMeta(pluginKey, 'hide');
    }
  }, [isOpen]);

  // Unlike tippy, the v3 BubbleMenu does not watch its own size. Reposition after React replaces
  // fallback content or wrapping changes, otherwise Floating UI keeps coordinates for the old size.
  useEffect(() => {
    if (!editor) return;

    const menu = menuRef.current;
    if (!menu) return;

    const observer = new ResizeObserver(() => {
      if (!menu.isConnected || editor.isDestroyed) return;
      editor.commands.setMeta(pluginKey, 'updatePosition');
    });
    observer.observe(menu);

    return () => observer.disconnect();
  }, [editor, pluginKey]);

  const shouldShow = useCallback<NonNullable<BubbleMenuProps['shouldShow']>>(
    (args) => {
      return (
        // Externally controlled open state. Assume open if undefined.
        (refAlternativeIsOpen.current?.(args) || (refIsOpen.current ?? true)) &&
        // Are we in edit mode?
        refIsEdit.current &&
        // Is the target node active (i.e. selected)?
        args.editor.isActive(nodeName) &&
        // Extra conditions if any
        (refExtraPredicate.current?.(args) ?? true)
      );
    },
    [nodeName]
  );

  const classes = useStyles();

  return (
    <BubbleMenu
      ref={menuRef}
      // A press that starts in the editor ends there, even when the menu it opens appears under the pointer.
      className="[.ProseMirror:active~&]:pointer-events-none"
      pluginKey={pluginKey}
      editor={editor ?? undefined}
      options={options}
      shouldShow={shouldShow}
      // No appendTo: mounting in the editor's parent makes the menu ride the scroll container.
      updateDelay={0}
      resizeDelay={0}
      // The plugin makes the wrapper a tab stop; announce it as a control group.
      role="group"
      // zIndex clears in-content overlays (table resize handles).
      style={{ maxWidth, zIndex: 10 }}
    >
      <DocumentControlsLock className="w-fit">
        <Card orientation="horizontal" className={mergeClasses(classes.card, className, classes[paddingCard])}>
          {leadingIcon}
          <CardHeader
            header={<div className={classes.cardHeader}>{header}</div>}
            action={<div className="flex">{children}</div>}
          />
        </Card>
      </DocumentControlsLock>
    </BubbleMenu>
  );
};

export default NodeBubbleMenuShell;
